import { useEffect, useMemo, useState } from 'react'
import settingService, { SETTING_GROUP_LABELS } from '../../services/settingService'
import formService from '../../services/formService'
import AutoAssignSettings from '../../components/admin/AutoAssignSettings'
import { HiOutlineSave, HiOutlineExternalLink, HiOutlinePhotograph } from 'react-icons/hi'
import toast from 'react-hot-toast'

function SiteSettingManagement() {
  const [items, setItems] = useState([])
  const [values, setValues] = useState({})   // key -> value đang chỉnh
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingKey, setUploadingKey] = useState(null)

  const uploadImage = async (key, file) => {
    if (!file) return
    setUploadingKey(key)
    try {
      const res = await formService.upload(file, 'image')
      if (res.status === 'success') { setVal(key, res.data.url); toast.success('Đã tải ảnh lên') }
    } catch { /* interceptor */ } finally { setUploadingKey(null) }
  }

  const load = async () => {
    setLoading(true)
    try {
      const res = await settingService.list()
      if (res.status === 'success') {
        setItems(res.data)
        setValues(res.data.reduce((a, s) => { a[s.key] = s.value ?? ''; return a }, {}))
      }
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const groups = useMemo(() => {
    const g = {}
    items.forEach((s) => { (g[s.group] = g[s.group] || []).push(s) })
    return g
  }, [items])

  const setVal = (key, v) => setValues((p) => ({ ...p, [key]: v }))

  const dirty = useMemo(
    () => items.some((s) => (s.value ?? '') !== (values[s.key] ?? '')),
    [items, values],
  )

  const save = async () => {
    setSaving(true)
    try {
      const changed = {}
      items.forEach((s) => { if ((s.value ?? '') !== (values[s.key] ?? '')) changed[s.key] = values[s.key] })
      const res = await settingService.bulkUpdate(changed)
      if (res.status === 'success') {
        toast.success('Đã lưu cấu hình')
        load()
      }
    } catch { /* interceptor */ } finally { setSaving(false) }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="page-title">Cấu hình hiển thị (CMS)</h1>
          <p className="page-subtitle">Phân luồng lead + thông tin hiển thị trên landing page: thương hiệu, hero, liên hệ, chân trang</p>
        </div>
        <button onClick={save} disabled={saving || !dirty} className="btn-primary">
          <HiOutlineSave className="w-5 h-5" /> {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
        </button>
      </div>

      <div className="mb-5">
        <AutoAssignSettings />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
      ) : (
        <div className="space-y-5">
          {Object.entries(groups).map(([group, rows]) => (
            <div key={group} className="card p-5">
              <h2 className="text-base font-semibold text-secondary-900 mb-4">
                {SETTING_GROUP_LABELS[group] || group}
              </h2>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                {rows.map((s) => (
                  <div key={s.key} className={s.input_type === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="label flex items-center justify-between">
                      <span>{s.label || s.key}</span>
                      <span className="text-2xs text-secondary-300 font-normal">{s.key}</span>
                    </label>

                    {s.input_type === 'textarea' ? (
                      <textarea className="input min-h-[110px] font-mono text-xs" value={values[s.key] ?? ''}
                        onChange={(e) => setVal(s.key, e.target.value)} />
                    ) : s.input_type === 'image' ? (
                      <div className="flex items-center gap-2">
                        <input className="input flex-1" value={values[s.key] ?? ''}
                          onChange={(e) => setVal(s.key, e.target.value)} placeholder="URL ảnh hoặc tải lên →" />
                        <label className="btn-secondary cursor-pointer whitespace-nowrap">
                          <HiOutlinePhotograph className="w-4 h-4" /> {uploadingKey === s.key ? 'Đang tải...' : 'Tải ảnh'}
                          <input type="file" accept="image/*" className="hidden"
                            onChange={(e) => uploadImage(s.key, e.target.files?.[0])} />
                        </label>
                      </div>
                    ) : (
                      <input className="input" value={values[s.key] ?? ''}
                        onChange={(e) => setVal(s.key, e.target.value)} />
                    )}

                    {s.input_type === 'image' && values[s.key] && (
                      <div className="mt-2">
                        <img src={values[s.key]} alt="preview" className="h-16 w-auto bg-secondary-50 rounded border border-secondary-100 object-contain" />
                      </div>
                    )}
                    {s.input_type === 'url' && values[s.key] && (
                      <a href={values[s.key]} target="_blank" rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-2xs text-primary-600">
                        Mở liên kết <HiOutlineExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <p className="text-2xs text-secondary-400">
            * Mục “Hệ thống văn phòng”: mỗi dòng theo định dạng <code className="text-secondary-500">Tên | Địa chỉ</code> — hiển thị ở chân trang.
          </p>
        </div>
      )}
    </div>
  )
}

export default SiteSettingManagement
